import React from "react";
import Image from "next/image";
import { NewContainer } from "../NewContainer";

const steps = [
  {
    num: "01",
    tag: "AI-Powered",
    tagType: "ai",
    title: "Connect your business apps & data collection",
    description:
      "In minutes, Ledgerowl securely connects to your financial tools and automatically ingests your data. Any additional information can be uploaded directly for our AI workflow to read and process.",
    visual: "apps",
  },
  {
    num: "02",
    tag: "AI + Human",
    tagType: "both",
    title: "AI builds your books. Humans close them.",
    description:
      "Our AI reconciles and organises your financial data automatically. Expert oversight by CAs and CPAs review and validate the results, ensuring your books are accurate, complete, and ready for reporting.",
    visual: "flow",
  },
  {
    num: "03",
    tag: "Human-Led",
    tagType: "human",
    title: "Financials ready for BAS, audits, and strategic decisions",
    description:
      "Accurate, verified books mean your financial statements are always ready for reporting, compliance, financing, or future growth.",
    visual: "doc",
  },
];

const AppsVisual = () => (
  <div className="bg-cream rounded-3xl p-12 border border-black/[0.04] w-full">
    <div className="flex flex-col gap-3">
      {[
        { logo: "/logos/xero.svg", name: "Xero" },
        { logo: "/logos/westpac.svg", name: "Westpac" },
        { logo: "/logos/stripe.svg", name: "Stripe" },
        { logo: "/logos/commbank.svg", name: "Commonwealth Bank" },
        { logo: "/logos/nab.svg", name: "NAB" },
      ].map((app) => (
        <div
          key={app.name}
          className="bg-base-white rounded-xl px-5 py-3.5 flex justify-between items-center text-[14px] font-medium"
        >
          <span className="flex items-center gap-2.5">
            <Image
              src={app.logo}
              alt={app.name}
              width={24}
              height={24}
              className="w-6 h-6 object-contain"
            />
            {app.name}
          </span>
          <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-cool-500 font-medium">
            <span className="w-2 h-2 bg-[#22C55E] rounded-full" />
            Connected
          </span>
        </div>
      ))}
    </div>
  </div>
);

const FlowVisual = () => (
  <div className="bg-cream rounded-3xl p-12 border border-black/[0.04] w-full">
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3.5 p-4 rounded-[14px] bg-base-white">
        <div className="w-10 h-10 rounded-[10px] bg-blue-primary-500/10 text-blue-primary-500 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" />
          </svg>
        </div>
        <div>
          <div className="text-[14px] font-medium text-gray-cool-700">
            AI auto-categorises 1,247 transactions
          </div>
          <div className="text-[12px] text-gray-cool-500 mt-0.5">
            99.2% categorised correctly in &lt; 2 minutes
          </div>
        </div>
      </div>
      <div className="text-center text-gray-cool-300 text-lg">↓</div>
      <div className="flex items-center gap-3.5 p-4 rounded-[14px] bg-base-white">
        <div className="w-10 h-10 rounded-[10px] bg-blue-primary-500/10 text-blue-primary-500 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div>
          <div className="text-[14px] font-medium text-gray-cool-700">
            AI flags 12 transactions for review
          </div>
          <div className="text-[12px] text-gray-cool-500 mt-0.5">
            Unusual amounts, missing references, duplicates
          </div>
        </div>
      </div>
      <div className="text-center text-gray-cool-300 text-lg">↓</div>
      <div className="flex items-center gap-3.5 p-4 rounded-[14px] bg-base-white">
        <div className="w-10 h-10 rounded-[10px] bg-textNoConnection/[0.08] text-textNoConnection flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div>
          <div className="text-[14px] font-medium text-gray-cool-700">
            Sarah reviews & resolves flagged items
          </div>
          <div className="text-[12px] text-gray-cool-500 mt-0.5">
            Professional judgement applied. Books finalised.
          </div>
        </div>
      </div>
      <div className="text-center text-gray-cool-300 text-lg">↓</div>
      <div className="flex items-center gap-3.5 p-4 rounded-[14px] bg-base-white">
        <div className="w-10 h-10 rounded-[10px] bg-[rgba(34,197,94,0.1)] text-[#22C55E] flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div>
          <div className="text-[14px] font-medium text-gray-cool-700">
            100% verified. Books ready.
          </div>
          <div className="text-[12px] text-gray-cool-500 mt-0.5">
            AI speed + human accuracy = flawless books
          </div>
        </div>
      </div>
    </div>
  </div>
);

const DocVisual = () => (
  <div className="bg-cream rounded-3xl p-8 lg:p-12 border border-black/[0.04] w-full">
    {/* Income Statement + Balance Sheet Grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
      {/* Income Statement */}
      <div className="bg-base-white rounded-[14px] p-5">
        <div className="flex justify-between items-center mb-3.5">
          <span className="font-bold text-[14px]">Income Statement</span>
          <span className="text-[#22C55E] text-[11px] font-semibold flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Final
          </span>
        </div>
        <div className="bg-white rounded-[10px] p-3.5 border border-black/[0.04] mb-2">
          <div className="text-[11px] text-gray-cool-500">Total Revenue</div>
          <div className="text-[20px] font-bold text-gray-cool-900">$2.4M</div>
          <div className="text-[11px] text-[#22C55E] mt-0.5">
            <svg className="w-2.5 h-2.5 inline mr-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            18% YoY
          </div>
        </div>
        <div className="bg-white rounded-[10px] p-3.5 border border-black/[0.04]">
          <div className="text-[11px] text-gray-cool-500">Net Profit</div>
          <div className="text-[20px] font-bold text-gray-cool-900">$380K</div>
          <div className="text-[11px] text-[#22C55E] mt-0.5">
            <svg className="w-2.5 h-2.5 inline mr-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            24% YoY
          </div>
        </div>
      </div>
      {/* Balance Sheet */}
      <div className="bg-base-white rounded-[14px] p-5">
        <div className="flex justify-between items-center mb-3.5">
          <span className="font-bold text-[14px]">Balance Sheet</span>
          <span className="text-[#22C55E] text-[11px] font-semibold flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Final
          </span>
        </div>
        <div className="bg-white rounded-[10px] p-3.5 border border-black/[0.04] mb-2">
          <div className="text-[11px] text-gray-cool-500">Total Assets</div>
          <div className="text-[20px] font-bold text-gray-cool-900">$1.8M</div>
        </div>
        <div className="bg-white rounded-[10px] p-3.5 border border-black/[0.04] mb-2">
          <div className="text-[11px] text-gray-cool-500">Total Liabilities</div>
          <div className="text-[20px] font-bold text-gray-cool-900">$620K</div>
        </div>
        <div className="bg-white rounded-[10px] p-3.5 border border-black/[0.04]">
          <div className="text-[11px] text-gray-cool-500">Equity</div>
          <div className="text-[20px] font-bold text-blue-primary-500">$1.18M</div>
        </div>
      </div>
    </div>
    {/* Report badges */}
    <div className="flex gap-2 flex-wrap mb-4">
      {[
        { icon: "💵", label: "Cash Flow Statement" },
        { icon: "📄", label: "BAS Report" },
        { icon: "🔍", label: "Audit Pack" },
      ].map((badge) => (
        <span
          key={badge.label}
          className="bg-base-white border border-black/[0.06] rounded-lg px-3 py-1.5 text-[12px] font-medium text-gray-cool-700 inline-flex items-center gap-1.5"
        >
          <span className="text-[10px]">{badge.icon}</span>
          {badge.label}
        </span>
      ))}
    </div>
    {/* Bottom cards */}
    <div className="flex gap-3 w-full">
      {[
        { emoji: "📊", title: "Yearly Audit", sub: "Audit-ready reports" },
        { emoji: "🤝", title: "M&A Ready", sub: "Due diligence packs" },
        { emoji: "🚀", title: "Growth", sub: "Strategic insights" },
      ].map((card) => (
        <div key={card.title} className="flex-1 bg-base-white rounded-xl p-4 text-center">
          <div className="text-[24px] mb-1.5">{card.emoji}</div>
          <div className="text-[13px] font-bold text-gray-cool-900">{card.title}</div>
          <div className="text-[11px] text-gray-cool-500 mt-0.5">{card.sub}</div>
        </div>
      ))}
    </div>
  </div>
);

const StepVisual = ({ type }) => {
  if (type === "apps") return <AppsVisual />;
  if (type === "flow") return <FlowVisual />;
  return <DocVisual />;
};

export default function PrimarySections() {
  return (
    <section id="how-it-works" className="py-[120px] px-6 lg:px-10 bg-cream">
      <div className="max-w-[1340px] mx-auto">
        <div className="mb-4 fade-in">
          <span className="inline-flex items-center gap-1.5 bg-blue-primary-500/[0.08] text-blue-primary-500 text-[13px] font-semibold px-4 py-1.5 rounded-full tracking-[0.5px]">
            How It Works
          </span>
        </div>
        <h2 className="font-inter text-[36px] leading-[46px] font-medium tracking-[-1.5px] mb-20 fade-in">
          AI speed. Human accountability. Three steps.
        </h2>

        <div className="flex flex-col gap-[100px]">
          {steps.map((step, i) => (
            <div
              key={step.num}
              className={`fade-in grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center ${
                i % 2 === 1 ? "lg:[direction:rtl]" : ""
              }`}
            >
              <div className={i % 2 === 1 ? "lg:[direction:ltr]" : ""}>
                <div className="text-[64px] font-extralight text-gray-cool-300 mb-3">
                  {step.num}
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.5px] px-3 py-1 rounded-full mb-4 ${
                    step.tagType === "ai"
                      ? "bg-blue-primary-500/[0.08] text-blue-primary-500"
                      : step.tagType === "human"
                        ? "bg-textNoConnection/[0.08] text-textNoConnection"
                        : "bg-gradient-to-r from-blue-primary-500/[0.08] to-textNoConnection/[0.08] text-[#0F1D2F]"
                  }`}
                >
                  {step.tag}
                </span>
                <h3 className="font-inter text-[28px] sm:text-[36px] lg:text-[42px] font-extrabold leading-[1.2] tracking-[-1px] mb-3">
                  {step.title}
                </h3>
                <div className="w-[60px] h-[2px] bg-blue-primary-500 my-4" />
                <p className="font-inter text-base leading-[1.8] text-gray-cool-500">
                  {step.description}
                </p>
              </div>
              <div className={i % 2 === 1 ? "lg:[direction:ltr]" : ""}>
                <StepVisual type={step.visual} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
